import { useEffect, useRef, useState } from 'react';
import * as faceapi from 'face-api.js';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MODEL_SOURCES = ['/models', 'https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js/weights'];
const CHECK_MS = 1500;
const emptyDelta = () => ({
  samples: 0, face_detected_samples: 0, eye_contact_samples: 0, face_absent_count: 0,
  multiple_faces_count: 0, face_swap_events: 0, face_match_sum: 0, face_match_samples: 0,
  tab_switch_count: 0, focus_loss_count: 0, multiple_voices_count: 0, audio_samples: 0,
});

const ear = (eye) => {
  const d = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  return (d(eye[1], eye[5]) + d(eye[2], eye[4])) / (2 * d(eye[0], eye[3]) || 1);
};

// Same criteria as the V1 room: centred, upright, not turned, not looking down/up, eyes open
const hasEyeContact = (det, vw, vh) => {
  const lm = det.landmarks; const box = det.detection.box;
  const avg = (pts) => ({ x: pts.reduce((s, p) => s + p.x, 0) / pts.length, y: pts.reduce((s, p) => s + p.y, 0) / pts.length });
  const L = avg(lm.getLeftEye()); const R = avg(lm.getRightEye()); const nose = lm.getNose()[3];
  const eyeDist = Math.hypot(R.x - L.x, R.y - L.y) || 1;
  const angle = Math.atan2(R.y - L.y, R.x - L.x) * 180 / Math.PI;
  const midX = (L.x + R.x) / 2; const midY = (L.y + R.y) / 2;
  const cx = (box.x + box.width / 2) / vw; const cy = (box.y + box.height / 2) / vh;
  const eyePos = (midY - box.y) / box.height; const noseV = (nose.y - midY) / box.height;
  const down = eyePos > 0.45 || noseV > 0.42 || box.y / vh < 0.08;
  const up = eyePos < 0.18;
  const open = (ear(lm.getLeftEye()) + ear(lm.getRightEye())) / 2 > 0.15;
  return cx > 0.15 && cx < 0.85 && cy > 0.15 && cy < 0.9 && Math.abs(angle) < 15 &&
    Math.abs((nose.x - midX) / eyeDist) < 0.25 && !down && !up && open;
};

export function useV2Proctoring({ videoRef, streamRef, token, active, currentInstanceId }) {
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [live, setLive] = useState({ faces: null, eyeContact: null });
  const deltaRef = useRef(emptyDelta());
  const refDescriptor = useRef(null);
  const absentStreak = useRef(0);
  const lastMulti = useRef(0);
  const lastSwap = useRef(0);
  const qRef = useRef(currentInstanceId);
  qRef.current = currentInstanceId;

  const logEvent = (event_type, details) => axios.post(`${API_URL}/api/v2/interviews/${token}/proctoring-event`, {
    event_type, question_instance_id: qRef.current || null, details: { ...details, timestamp: new Date().toISOString() },
  }).catch(() => {});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const src of MODEL_SOURCES) {
        try {
          await faceapi.nets.tinyFaceDetector.loadFromUri(src);
          await faceapi.nets.faceLandmark68Net.loadFromUri(src);
          await faceapi.nets.faceRecognitionNet.loadFromUri(src);
          if (!cancelled) setModelsLoaded(true);
          return;
        } catch (e) { console.warn('[V2 proctoring] model source failed', src, e); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Face presence, multiple faces, continuity (swap), eye contact/head pose
  useEffect(() => {
    if (!active || !modelsLoaded) return;
    const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.5 });
    const id = setInterval(async () => {
      const v = videoRef.current;
      if (!v || v.readyState < 2) return;
      try {
        const dets = await faceapi.detectAllFaces(v, opts).withFaceLandmarks().withFaceDescriptors();
        const d = deltaRef.current; d.samples += 1;
        const now = Date.now();
        if (dets.length === 0) {
          absentStreak.current += 1;
          if (absentStreak.current === 3) { d.face_absent_count += 1; logEvent('face_absent', {}); }
          setLive({ faces: 0, eyeContact: false });
          return;
        }
        absentStreak.current = 0; d.face_detected_samples += 1;
        const primary = dets.reduce((a, b) => (a.detection.box.area > b.detection.box.area ? a : b));
        const others = dets.filter((x) => x !== primary && x.detection.box.area / primary.detection.box.area > 0.15 && x.detection.score > 0.55);
        if (others.length && now - lastMulti.current > 10000) {
          lastMulti.current = now; d.multiple_faces_count += 1; logEvent('multiple_faces', { count: others.length + 1 });
        }
        if (!refDescriptor.current) refDescriptor.current = primary.descriptor;
        const dist = faceapi.euclideanDistance(refDescriptor.current, primary.descriptor);
        d.face_match_sum += Math.max(0, Math.round((1 - dist) * 100)); d.face_match_samples += 1;
        if (dist > 0.6 && now - lastSwap.current > 10000) {
          lastSwap.current = now; d.face_swap_events += 1; logEvent('face_swap', { distance: dist.toFixed(3) });
        }
        const eye = hasEyeContact(primary, v.videoWidth || 640, v.videoHeight || 480);
        if (eye) d.eye_contact_samples += 1;
        setLive({ faces: dets.length, eyeContact: eye });
      } catch (e) { console.warn('[V2 proctoring] detection error', e); }
    }, CHECK_MS);
    return () => clearInterval(id);
  }, [active, modelsLoaded, videoRef]);

  // Voice analysis (V1 heuristic: pitch deviation from baseline / multiple spectral peaks)
  useEffect(() => {
    const stream = streamRef.current;
    if (!active || !stream || !stream.getAudioTracks().length) return;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = ctx.createAnalyser(); analyser.fftSize = 2048;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const base = { pitch: 0, n: 0 }; let streak = 0;
    const id = setInterval(() => {
      const data = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(data);
      const avg = data.reduce((a, b) => a + b, 0) / data.length;
      deltaRef.current.audio_samples += 1;
      if (avg < 15) return;
      const lo = Math.floor(85 * data.length / (ctx.sampleRate / 2)); const hi = Math.floor(3400 * data.length / (ctx.sampleRate / 2));
      const voice = data.slice(lo, hi); let max = 0; let idx = 0;
      voice.forEach((x, i) => { if (x > max) { max = x; idx = i; } });
      const freq = (lo + idx) * (ctx.sampleRate / 2) / data.length;
      if (base.n < 20) { base.pitch = (base.pitch * base.n + freq) / (base.n + 1); base.n += 1; return; }
      const deviates = Math.abs(freq - base.pitch) / base.pitch > 0.4 && max > 50;
      streak = deviates ? streak + 1 : 0;
      if (streak === 3) { deltaRef.current.multiple_voices_count += 1; logEvent('multiple_voices', { freq: Math.round(freq) }); }
    }, 500);
    return () => { clearInterval(id); ctx.close().catch(() => {}); };
  }, [active, streamRef.current]);

  // Tab switching (visibility) and window focus changes
  useEffect(() => {
    if (!active) return;
    const onVis = () => { if (document.hidden) { deltaRef.current.tab_switch_count += 1; logEvent('tab_switch', {}); } };
    const onBlur = () => setTimeout(() => { if (!document.hidden) { deltaRef.current.focus_loss_count += 1; logEvent('focus_loss', {}); } }, 300);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('blur', onBlur);
    return () => { document.removeEventListener('visibilitychange', onVis); window.removeEventListener('blur', onBlur); };
  }, [active]);

  const takeDelta = () => {
    const d = { ...deltaRef.current, models_loaded: modelsLoaded };
    deltaRef.current = emptyDelta();
    return d;
  };

  return { modelsLoaded, live, takeDelta };
}
